/**
 * Citron product page, above the fold.
 *
 * Owns four things Horizon has no block for: the two modals, the gift-wrap
 * line item, the sticky bar, and keeping the hand-built price row in sync with
 * a variant change.
 *
 * Adding to cart is deliberately a single POST that mirrors, field for field,
 * what Horizon's product-form.js sends (`{items, sections}` to the cart-add
 * route, then a `cart:update` event with `source: 'product-form-component'`).
 * That is what lets one request carry the watch and the gift wrap together,
 * while the drawer, the cart sections and the header count still refresh off
 * the theme's own listeners.
 */
(function () {
  'use strict';

  var CART_UPDATE = 'cart:update';
  var VARIANT_UPDATE = 'variant:update';

  function routes() {
    var r = (window.Theme && window.Theme.routes) || {};
    return {
      add: r.cart_add_url || '/cart/add',
      cart: r.cart_url ? r.cart_url + '.js' : '/cart.js',
    };
  }

  /* ====================================================================== 1
     Modal: Esc, overlay click, close button, focus trap, focus restore.
     ====================================================================== */

  var FOCUSABLE =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  var openModal = null;
  var lastFocused = null;

  function focusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
  }

  function trap(event) {
    if (!openModal || event.key !== 'Tab') return;
    var items = focusables(openModal);
    if (!items.length) {
      event.preventDefault();
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    var inside = openModal.contains(document.activeElement);
    // Focus can sit outside the card -- after a click on the overlay, or if the
    // initial focus lost a race -- so pull it back in from whichever end the
    // user is heading towards rather than letting Tab walk the page behind.
    if (!inside) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function onKeydown(event) {
    if (!openModal) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    trap(event);
  }

  function open(modal) {
    if (!modal || openModal === modal) return;
    if (openModal) close();
    lastFocused = document.activeElement;
    openModal = modal;
    modal.classList.add('is-open');
    modal.removeAttribute('aria-hidden');
    document.documentElement.style.overflow = 'hidden';
    /* One frame later, deliberately. The modal is visibility:hidden until the
       class lands, and a visibility:hidden element cannot take focus -- calling
       focus() in the same tick silently left focus on the trigger. */
    requestAnimationFrame(function () {
      if (openModal !== modal) return;
      var items = focusables(modal);
      var target = modal.querySelector('[data-ctr-modal-close]') || items[0];
      if (target) target.focus();
    });
  }

  function close() {
    if (!openModal) return;
    var modal = openModal;
    openModal = null;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (lastFocused && lastFocused.focus) lastFocused.focus();
    lastFocused = null;
  }

  function wireModals(scope) {
    scope.querySelectorAll('[data-ctr-modal-open]').forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        open(document.getElementById(btn.getAttribute('data-ctr-modal-open')));
      });
    });

    document.querySelectorAll('.ctr-pdp-modal').forEach(function (modal) {
      modal.setAttribute('aria-hidden', 'true');
      modal.addEventListener('click', function (event) {
        // Only the overlay itself, never a click that started inside the card.
        if (event.target === modal) close();
      });
      modal.querySelectorAll('[data-ctr-modal-close]').forEach(function (btn) {
        btn.addEventListener('click', function (event) {
          event.preventDefault();
          close();
        });
      });
    });

    document.addEventListener('keydown', onKeydown);
  }

  /* ====================================================================== 2
     Add to cart / buy it now.
     ====================================================================== */

  function currentVariantId(scope) {
    var fromUrl = new URL(window.location.href).searchParams.get('variant');
    if (fromUrl) return fromUrl;
    var input = document.querySelector('product-form-component input[name="id"]');
    if (input && input.value) return input.value;
    return scope.getAttribute('data-default-variant') || '';
  }

  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      ids.push(el.dataset.sectionId);
    });
    return ids.join(',');
  }

  function busy(btn, on) {
    if (!btn) return;
    btn.classList.toggle('is-busy', on);
    if (on) btn.setAttribute('aria-busy', 'true');
    else btn.removeAttribute('aria-busy');
    btn.disabled = on;
  }

  function showError(scope, message) {
    var box = scope.querySelector('[data-ctr-pdp-error]');
    if (!box) return;
    box.textContent = message || '';
    if (message) {
      clearTimeout(box.ctrTimer);
      box.ctrTimer = setTimeout(function () {
        box.textContent = '';
      }, 6000);
    }
  }

  /**
   * One request for the watch and, when ticked, the gift wrap.
   * @returns {Promise<boolean>} whether the add succeeded
   */
  function addToCart(scope, btn) {
    var variantId = currentVariantId(scope);
    if (!variantId) {
      showError(scope, 'Please choose an option first.');
      return Promise.resolve(false);
    }

    var items = [{ id: Number(variantId), quantity: 1 }];
    var wrap = scope.querySelector('[data-ctr-giftwrap]');
    if (wrap && wrap.checked && wrap.value) {
      items.push({ id: Number(wrap.value), quantity: 1 });
    }

    var r = routes();
    busy(btn, true);
    showError(scope, '');

    return fetch(r.add, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items, sections: cartSectionIds() }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        if (response.status) {
          showError(scope, response.description || response.message || 'Could not add to cart.');
          return false;
        }
        return fetch(r.cart, { headers: { Accept: 'application/json' } })
          .then(function (res) {
            return res.json();
          })
          .catch(function () {
            return null;
          })
          .then(function (cart) {
            document.dispatchEvent(
              new CustomEvent(CART_UPDATE, {
                bubbles: true,
                detail: {
                  resource: cart || undefined,
                  sourceId: 'ctr-pdp',
                  data: {
                    source: 'product-form-component',
                    itemCount: items.length,
                    productId: scope.getAttribute('data-product-id') || '',
                    sections: response.sections,
                  },
                },
              })
            );
            return true;
          });
      })
      .catch(function (error) {
        console.error('[citron-pdp] add to cart failed', error);
        showError(scope, 'Something went wrong. Please try again.');
        return false;
      })
      .then(function (ok) {
        busy(btn, false);
        return ok;
      });
  }

  function openDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    // The drawer opens itself on cart:update when it carries auto-open; this is
    // only for the case where the merchant has that switched off, which the
    // brief overrides ("open the cart drawer after a successful add").
    if (!drawer || typeof drawer.open !== 'function' || drawer.hasAttribute('auto-open')) return;
    /* Deferred: the cart items morph inside a view transition on the same event,
       and opening the dialog mid-transition aborts it. Horizon's
       startViewTransition never catches transition.finished, so the abort
       surfaces as an unhandled rejection in the console. */
    setTimeout(function () {
      drawer.open();
    }, 300);
  }

  function wireButtons(scope) {
    scope.querySelectorAll('[data-ctr-atc]').forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        addToCart(scope, btn).then(function (ok) {
          if (ok) openDrawer();
        });
      });
    });

    scope.querySelectorAll('[data-ctr-buynow]').forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        addToCart(scope, btn).then(function (ok) {
          if (ok) window.location.href = '/checkout';
        });
      });
    });
  }

  /* ====================================================================== 3
     Sticky bar: shown once the main buy buttons leave the viewport.
     ====================================================================== */

  function wireStickyBar(scope) {
    var bar = document.querySelector('[data-ctr-pdp-bar]');
    var anchor = scope.querySelector('[data-ctr-buys]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(
      function (entries) {
        var entry = entries[0];
        /* Only once the buttons have gone off the TOP. On a 375px viewport they
           start below the fold, so "not intersecting" on its own showed the bar
           before the shopper had scrolled at all. */
        var visible = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        bar.classList.toggle('is-visible', visible);
        document.body.classList.toggle('ctr-pdp-bar-open', visible);
      },
      { threshold: 0 }
    );
    io.observe(anchor);
  }

  /* ====================================================================== 4
     Variant sync. Horizon re-fetches the whole section and hands us the parsed
     document; every region marked data-ctr-sync is swapped from it, so the
     price row, the discount pill and the sticky bar's figures all follow the
     variant without a second request.
     ====================================================================== */

  function wireVariantSync(scope) {
    document.addEventListener(VARIANT_UPDATE, function (event) {
      var html = event.detail && event.detail.data && event.detail.data.html;
      if (!html) return;

      document.querySelectorAll('[data-ctr-sync]').forEach(function (node) {
        var key = node.getAttribute('data-ctr-sync');
        var fresh = html.querySelector('[data-ctr-sync="' + key + '"]');
        if (fresh) node.innerHTML = fresh.innerHTML;
      });

      // The gallery follows the variant through Horizon's own media-gallery
      // listener; only the thumbnails' active state needs a nudge when the
      // slide was changed programmatically.
      var variant = event.detail.resource;
      if (variant && variant.featured_media) {
        var bar = document.querySelector('[data-ctr-pdp-bar] .ctr-pdp-bar__img');
        var fresh = html.querySelector('[data-ctr-pdp-bar] .ctr-pdp-bar__img');
        if (bar && fresh && fresh.getAttribute('src')) bar.setAttribute('src', fresh.getAttribute('src'));
      }
    });
  }

  /* ====================================================================== 5
     Stars scroll to the reviews section.
     ====================================================================== */

  /* Delegated, because the stars sit inside a data-ctr-sync region whose
     innerHTML is replaced on every variant change. */
  function wireStars() {
    document.addEventListener('click', function (event) {
      var btn = event.target.closest && event.target.closest('[data-ctr-reviews-jump]');
      if (!btn) return;
      event.preventDefault();
      var target =
        document.querySelector('#reviews, [id*="judgeme"], .jdgm-rev-widget, .jdgm-widget, #shopify-block-reviews') ||
        document.querySelector('[data-ctr-reviews]');
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /* ====================================================================== 6
     Variant tiles: Horizon renders a radio per option value; the selected and
     unavailable classes are applied here so the CSS has something stable to
     hook, and re-applied after every morph.
     ====================================================================== */

  function paintTiles() {
    document.querySelectorAll('.variant-option__button-label--tile').forEach(function (label) {
      var input = label.querySelector('input');
      if (!input) return;
      label.classList.toggle('is-selected', input.checked);
      label.classList.toggle('is-unavailable', input.dataset.optionAvailable === 'false');
    });
  }

  /* ====================================================================== init */

  function init() {
    var scope = document.querySelector('[data-ctr-pdp]');
    if (!scope) return;

    wireModals(scope);
    wireButtons(scope);
    wireStickyBar(scope);
    wireVariantSync(scope);
    wireStars();
    paintTiles();

    // The picker is morphed in place on every variant change, which drops the
    // classes above.
    document.addEventListener(VARIANT_UPDATE, function () {
      setTimeout(paintTiles, 0);
    });
    document.addEventListener('change', function (event) {
      if (event.target && event.target.closest && event.target.closest('variant-picker')) paintTiles();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
